/**
 * Shared combat data — single source of truth for creatures, boss icons, and combat constants.
 *
 * Consumers: BossSession, BossLab, DeckLevels, BossPreview
 * Roster source of truth: backend/services/mid_boss_roster.py and
 * backend/services/final_boss_roster.py.
 * Regenerate frontend roster JSON with: npm run generate:rosters
 */
import React from 'react';
import rosters from './generated/rosters.json';

// ─── Creature Definitions ──────────────────────────────────────────────

export const CREATURES = rosters.mid_bosses.map((boss) => ({ ...boss }));

// Hash-fallback assignment pool — mirrors backend ROTATION_POOL
// (mid_boss_roster.py:332): DLC-only bosses stay OUT of the deck hash.
// Hashing across all of CREATURES drifted from the backend when the roster
// grew DLC entries (preview showed bosses the server would never assign).
export const ROTATION_POOL = CREATURES.filter((boss) => !boss.dlc_only);

// ─── Creature Assignment (matches backend get_mid_boss_for_deck) ───────

function utf8Bytes(value) {
  if (typeof TextEncoder !== 'undefined') {
    return Array.from(new TextEncoder().encode(String(value)));
  }
  return Array.from(String(value), (char) => char.charCodeAt(0) & 0xff);
}

function rightRotate(value, amount) {
  return (value >>> amount) | (value << (32 - amount));
}

function sha256Hex(value) {
  const bytes = utf8Bytes(value);
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while ((bytes.length % 64) !== 56) bytes.push(0);
  const high = Math.floor(bitLength / 0x100000000);
  const low = bitLength >>> 0;
  bytes.push(
    (high >>> 24) & 0xff, (high >>> 16) & 0xff, (high >>> 8) & 0xff, high & 0xff,
    (low >>> 24) & 0xff, (low >>> 16) & 0xff, (low >>> 8) & 0xff, low & 0xff
  );

  const k = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  let h0 = 0x6a09e667; let h1 = 0xbb67ae85; let h2 = 0x3c6ef372; let h3 = 0xa54ff53a;
  let h4 = 0x510e527f; let h5 = 0x9b05688c; let h6 = 0x1f83d9ab; let h7 = 0x5be0cd19;

  for (let offset = 0; offset < bytes.length; offset += 64) {
    const w = new Array(64);
    for (let i = 0; i < 16; i += 1) {
      const j = offset + i * 4;
      w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) >>> 0;
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = (rightRotate(w[i - 15], 7) ^ rightRotate(w[i - 15], 18) ^ (w[i - 15] >>> 3)) >>> 0;
      const s1 = (rightRotate(w[i - 2], 17) ^ rightRotate(w[i - 2], 19) ^ (w[i - 2] >>> 10)) >>> 0;
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let a = h0; let b = h1; let c = h2; let d = h3;
    let e = h4; let f = h5; let g = h6; let h = h7;
    for (let i = 0; i < 64; i += 1) {
      const s1 = (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) >>> 0;
      const ch = ((e & f) ^ (~e & g)) >>> 0;
      const temp1 = (h + s1 + ch + k[i] + w[i]) >>> 0;
      const s0 = (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) >>> 0;
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
      const temp2 = (s0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + temp1) >>> 0;
      d = c; c = b; b = a; a = (temp1 + temp2) >>> 0;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((part) => part.toString(16).padStart(8, '0'))
    .join('');
}

export function getCreatureForDeck(deckId) {
  const n = parseInt(sha256Hex(String(deckId)).slice(0, 8), 16);
  return ROTATION_POOL[n % ROTATION_POOL.length];
}

// ─── Creature SVG Icons ────────────────────────────────────────────────
// Factory: returns icon map at given size. Combat views use 48, map/preview use 28.

export function makeCreatureIcons(size = 28) {
  const s = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const atheroPlaceholderIcon = (
    <svg {...s}>
      <path d="M18 6c0-1.5-1.5-3-3.5-3S11 4 11 6c0 1.5 1 2.5 1 4s-2 3-4 3-4 1.5-4 3.5S5.5 20 8 20s4-1 6-1 4 .5 4 2" />
      <circle cx="16" cy="4.5" r="0.8" fill="currentColor" stroke="none" />
      <path d="M19 5l1.5-1M19 5l1.5 1" />
      <path d="M7 17c2-1.5 4-2 6-1" opacity="0.5" />
    </svg>
  );
  // Ten Shin, the Resistant — a hooded shinobi (the "silent killer" of resistant HTN):
  // hood + shinobi headband + furrowed slit eyes + face mask. currentColor so it tints
  // crimson from --creature-color, matching the detailed foil-card portrait.
  const tenShinNinjaIcon = (
    <svg {...s}>
      <path d="M12 3C8.7 3 6 5.7 6 9v4c0 3.3 2.7 6 6 6s6-2.7 6-6V9c0-3.3-2.7-6-6-6z" />
      <path d="M5.6 10h12.8" />
      <path d="M7.7 12.8l2.6 1.4M16.3 12.8l-2.6 1.4" />
      <path d="M8 16.4h8" opacity="0.5" />
    </svg>
  );
  return {
    Athero: atheroPlaceholderIcon,
    TenShin: tenShinNinjaIcon,
    RaasWarden: atheroPlaceholderIcon,
    CalciumGate: atheroPlaceholderIcon,
    VolumeWarden: atheroPlaceholderIcon,
    AldosteroneLock: atheroPlaceholderIcon,
    AdrenergicSentry: atheroPlaceholderIcon,
    Anaphyl: (
      <svg {...s}>
        <path d="M8 21l8-18" />
        <path d="M4 13c2-2 4-3 8-3s6 1 8 3" />
        <path d="M6 16c2 2 4 3 6 3s4-1 6-3" />
        <path d="M8 7c1.5-2 6.5-2 8 0" />
        <circle cx="9" cy="13" r="1" fill="currentColor" stroke="none" />
        <circle cx="15" cy="13" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
    // Pestil, the Selector (NAPLEX Infectious Disease) — petri dish + resistant
    // colonies (selection pressure). Placeholder matching the stroke style;
    // refine in the combat feel-check if desired.
    Pestil: (
      <svg {...s}>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="6.4" opacity="0.3" />
        <circle cx="9.5" cy="10" r="1.1" fill="currentColor" stroke="none" />
        <circle cx="14.2" cy="11" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="10.8" cy="14.4" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="14.6" cy="14.6" r="0.6" fill="currentColor" stroke="none" />
      </svg>
    ),
    Defeated: (
      <svg {...s}>
        <path d="M3 18h18v2H3z" />
        <path d="M3 18l3-10 3 4 3-6 3 6 3-4 3 10" />
      </svg>
    ),
  };
}

// ─── Final Boss Icons ──────────────────────────────────────────────────
// Canonical SVGs — must match what LearnHub.jsx renders in production.

export function makeFinalBossIcons(size = 40) {
  const s = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  return {
    Septyx: (
      <svg {...s}>
        <path d="M12 2C8 2 5 5.5 5 10v6c0 2 1.5 4 3.5 5L12 22l3.5-1c2-1 3.5-3 3.5-5v-6c0-4.5-3-8-7-8z" />
        <path d="M8 8c1.2-1 2.5-1.5 4-1.5S14.8 7 16 8" />
        <circle cx="10" cy="11" r="1.2" fill="currentColor" stroke="none" />
        <circle cx="14" cy="11" r="1.2" fill="currentColor" stroke="none" />
        <path d="M8.5 16h7" opacity="0.35" strokeDasharray="2 2" />
        <path d="M12 3v18" opacity="0.2" />
      </svg>
    ),
  };
}

// ─── Final Boss Data ────────────────────────────────────────────────────
export const MAIN_BOSSES = rosters.final_bosses.map((boss) => ({ ...boss }));

// Single-color lookup used by BossLab and BossSession
export const FINAL_BOSS_COLORS = Object.fromEntries(MAIN_BOSSES.map(b => [b.name, b.colors[0]]));

const MID_BOSS_CLASS_COLORS = Object.fromEntries(
  CREATURES.flatMap((boss) => (boss.classes || [boss.class]).map((className) => [className, boss.color]))
);
const FINAL_BOSS_CLASS_COLORS = Object.fromEntries(
  MAIN_BOSSES.flatMap((boss) => (boss.classes || []).map((className, index) => [
    className,
    (boss.colors && boss.colors[index]) || boss.color,
  ]))
);

// trickster/blazer are retained for legacy card effects.
export const CLASS_COLORS = {
  ...MID_BOSS_CLASS_COLORS,
  ...FINAL_BOSS_CLASS_COLORS,
  trickster: '#10b981',
  blazer:    '#ef4444',
};

// ─── Enemy tooltip resolver ────────────────────────────────────────────────
// Drives the shared combat tooltip (CombatTooltipPanel) for enemy strip pills.
// Resolves an enemy's identity color, canonical icon, and mechanic blurb from
// (1) fields the backend now ships on each cross-enemy member (boss_class,
// description) and (2) the roster as a fallback for top-level bosses. Swarm
// minis carry their own backend `description` (ability_summary); roster lookups
// add an icon/color for the named bosses (Athero, Anaphyl, Septyx).
const ENEMY_TT_CREATURE_ICONS = makeCreatureIcons(18);
const ENEMY_TT_FINAL_ICONS = makeFinalBossIcons(18);
const ROSTER_BY_CLASS = (() => {
  const map = {};
  CREATURES.forEach((c) => {
    (c.classes || [c.class]).forEach((cl) => { if (cl) map[cl] = c; });
    if (c.pool) map[c.pool] = c;
  });
  MAIN_BOSSES.forEach((b) => {
    (b.classes || []).forEach((cl) => { if (cl) map[cl] = b; });
  });
  return map;
})();

// Canonical enemy-TIER colors are intentionally a tooltip-layer override for
// this scoped campaign roster. The backend roster colors encode HTN axis
// identity and are reused by the skill tree, so changing them would recolor
// unrelated surfaces. Add future tooltip-tier enemies here instead.
const TIER_TOOLTIP_COLOR = Object.freeze({
  1: '#ef4444',
  2: '#f97316',
  3: '#fbbf24',
});
const TIER_TOOLTIP_TIER_BY_STABLE_KEY = Object.freeze({
  ten_shin: 2,
  raas_warden: 3,
  calcium_gate: 3,
  volume_warden: 3,
  aldosterone_lock: 3,
  adrenergic_sentry: 3,
});
const TIER_TOOLTIP_TIER_BY_CANONICAL_NAME = Object.freeze({
  Septyx: 1,
});

function resolveEnemyTooltipTier(enemy, rosterEntry) {
  const stableKeys = [enemy.boss_class, enemy.slug, enemy.base_slug];
  for (const key of stableKeys) {
    const tier = TIER_TOOLTIP_TIER_BY_STABLE_KEY[key];
    if (tier) return tier;
  }

  // Septyx currently has no unique slug/class: runtime sends `phantom` plus
  // its canonical name, while the generated roster links `phantom` to Septyx.
  // Gate the name fallback through that linkage when a runtime name is absent
  // so another named phantom cannot accidentally inherit the final-boss red.
  const runtimeName = String(enemy.base_name || enemy.name || '').trim();
  if (TIER_TOOLTIP_TIER_BY_CANONICAL_NAME[runtimeName]) {
    return TIER_TOOLTIP_TIER_BY_CANONICAL_NAME[runtimeName];
  }
  if (!runtimeName && rosterEntry?.name === 'Septyx') {
    return TIER_TOOLTIP_TIER_BY_CANONICAL_NAME.Septyx;
  }
  return null;
}

export function getEnemyTooltipInfo(enemy = {}) {
  const cls = enemy.boss_class || enemy.slug || '';
  const rosterEntry = ROSTER_BY_CLASS[cls] || ROSTER_BY_CLASS[enemy.slug] || null;
  const tierColor = TIER_TOOLTIP_COLOR[resolveEnemyTooltipTier(enemy, rosterEntry)];
  const color = tierColor
    || enemy.color
    || (rosterEntry && ((rosterEntry.colors && rosterEntry.colors[0]) || rosterEntry.color))
    || CLASS_COLORS[cls]
    || CLASS_COLORS[enemy.slug]
    || (enemy.is_main_boss ? '#ef4444' : '#f87171');
  const iconKey = rosterEntry && rosterEntry.icon;
  const icon = (iconKey && (ENEMY_TT_CREATURE_ICONS[iconKey] || ENEMY_TT_FINAL_ICONS[iconKey])) || null;
  const description = String(enemy.description || (rosterEntry && rosterEntry.description) || '').trim();
  return { color, icon, description };
}

// ─── Combat Stats (mirrors backend/services/combat_engine.py) ──────────

export const MINI_BOSS_BASE = {
  boss_hp: 150, user_hp: 100,
  correct_damage: 10, incorrect_damage: 8,
  attack_damage: 5, dormant_base: 10.0, attack_interval: 15.0,
};

export const FINAL_BOSS_BASE = {
  boss_hp: 150, user_hp: 120,
  correct_damage: 10, incorrect_damage: 10,
  attack_damage: 8, dormant_base: 15.0, attack_interval: 10.0,
};

export function calcBossHp(baseHp, weaknessPct) {
  return Math.max(80, Math.floor(baseHp * (1.0 - weaknessPct * 0.003)));
}

// ─── Streak Bonus Damage (mirrors backend STREAK_BONUS) ────────────────
// Consecutive correct answers grant additive bonus damage.
export const STREAK_BONUS = [
  [10, 8],   // 10+ streak: +8 bonus
  [8,  6],   //  8+ streak: +6 bonus
  [5,  4],   //  5+ streak: +4 bonus
  [3,  2],   //  3+ streak: +2 bonus
];

export function getStreakBonus(streak) {
  for (const [threshold, bonus] of STREAK_BONUS) {
    if (streak >= threshold) return bonus;
  }
  return 0;
}

// ─── Streak Flame Icon ──────────────────────────────────────────────

export function StreakFlameIcon({ size = 14 }) {
  // Simple stroke-based flame outline (Lucide-style) — matches the system
  // icon language (1.5 stroke weight, rounded caps, currentColor) used by
  // MapCombatPanel's boss / drill / dialysis glyphs and the FAB-region
  // pill icons. Drops the closed filled-path teardrop shape.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  );
}

// ─── Intermission Messages (per creature/boss) ───────────────────────

const [ANAPHYL_INTERMISSION_BOSS, ATHERO_INTERMISSION_BOSS] = CREATURES;
const [FINAL_INTERMISSION_BOSS] = MAIN_BOSSES;

export const INTERMISSION_MESSAGES = {
  [ANAPHYL_INTERMISSION_BOSS.name]: `${ANAPHYL_INTERMISSION_BOSS.name.split(',')[0]} resets the cascade and surges back in...`,
  [ATHERO_INTERMISSION_BOSS.name]: `${ATHERO_INTERMISSION_BOSS.name.split(',')[0]} reforms the plaque front and presses deeper...`,
  [FINAL_INTERMISSION_BOSS.name]: `${FINAL_INTERMISSION_BOSS.name} is not finished with you yet...`,
};

export function getIntermissionMessage(bossName) {
  return INTERMISSION_MESSAGES[bossName] || `${bossName} is not finished with you yet...`;
}

// ─── Question Block Types ──────────────────────────────────────────────

export const QUESTION_TYPES = ['mcq', 'fill_blank', 'true_false', 'matching', 'frq', 'case_study', 'multi_select', 'diagram_build'];
